import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getDateFormat,
  getTimeFormat,
  getUserTimezone,
  setLocaleSettings,
} from '../utils/timezone';

/**
 * useGeneralSettings — shared hook for the user's General Settings
 * (Date Format / Time Format / Timezone) used for timestamp display.
 *
 * Replaces the duplicated `api.get('/settings/general')`-in-effect pattern that
 * previously lived in Layout, CommandCenter, DeviceManagement, UserManagement,
 * ImpersonationAudit and LiveTracking.
 *
 * - Initial value is read synchronously from the `timezone.js` cache (populated
 *   on login) so there is no default→actual flicker.
 * - Refreshes once on mount from `GET /settings/general` (async/await, so it
 *   never sets state synchronously inside the effect — keeps the React Compiler
 *   `set-state-in-effect` lint happy) and keeps the `timezone.js` cache in sync.
 *
 * @param {Object} [opts]
 * @param {boolean} [opts.enabled=true] - When false, skips the network refresh
 *   (e.g. Layout only needs the impersonated account's settings while impersonating).
 * @returns {{ dateFormat: string, timeFormat: string, timezone: string }}
 */
export const useGeneralSettings = ({ enabled = true } = {}) => {
  const { api } = useAuth();
  const [settings, setSettings] = useState(() => ({
    dateFormat: getDateFormat() || 'DD/MM/YYYY',
    timeFormat: getTimeFormat() || '24h',
    timezone: getUserTimezone() || '',
  }));

  useEffect(() => {
    if (!enabled || !api) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/settings/general');
        if (cancelled) return;
        const next = {
          dateFormat: res.data?.date_format || 'DD/MM/YYYY',
          timeFormat: res.data?.time_format || '24h',
          timezone: res.data?.timezone || '',
        };
        setSettings(next);
        // Keep the shared formatDT()/formatSpeed()/formatDistance() cache in sync.
        setLocaleSettings({
          dateFormat: next.dateFormat,
          timeFormat: next.timeFormat,
          timezone: next.timezone,
          distanceUnit: res.data?.distance_unit || 'km',
          speedUnit: res.data?.speed_unit || 'kmh',
        });
      } catch { /* ignore — keep cached/default values */ }
    })();
    return () => { cancelled = true; };
  }, [api, enabled]);

  return settings;
};

export default useGeneralSettings;
